/**
 * <LessonContent> — rend du Markdown enrichi de KaTeX (server-side).
 *
 * Le Markdown supporte la syntaxe usuelle (h1-h3, gras, italique, listes, code,
 * liens) plus :
 *   - $...$    → math inline KaTeX
 *   - $$...$$  → math block KaTeX
 *
 * Sécurité : on rend le Markdown via `marked` puis on injecte. Pour éviter qu'un
 * admin compromis ou que des seeds polluées injectent du JS, on peut wrapper
 * dans un sanitizer (DOMPurify) plus tard. Pour l'instant Sprint 2, le seul
 * input vient de la table `lessons` protégée par RLS admin-only.
 *
 * ⚠️ Importer 'katex/dist/katex.min.css' au niveau du layout consommateur.
 */

import katex from 'katex';
import { Marked } from 'marked';
import { cn } from '../lib/utils';

const KATEX_OPTIONS_INLINE = {
  displayMode: false,
  output: 'html' as const,
  strict: 'ignore' as const,
  trust: false,
  throwOnError: false,
  errorColor: '#EF4444',
  maxExpand: 1000,
  maxSize: 500,
};

const KATEX_OPTIONS_BLOCK = { ...KATEX_OPTIONS_INLINE, displayMode: true };

/**
 * Renvoie le HTML KaTeX rendu, ou un fallback `<code>` si parsing fail.
 * `false` au throwOnError pour éviter de planter tout le rendu Markdown.
 */
function renderMathSafe(src: string, block: boolean): string {
  try {
    return katex.renderToString(src, block ? KATEX_OPTIONS_BLOCK : KATEX_OPTIONS_INLINE);
  } catch {
    const escaped = escapeHtml(src);
    return `<code class="text-error-600">${escaped}</code>`;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Pré-traitement : remplace les blocs math par des placeholders neutres pour
 * que `marked` ne les casse pas, puis on ré-injecte après le rendu.
 *
 * Ordre crucial : $$...$$ AVANT $...$ pour ne pas matcher `$x$` à l'intérieur de `$$...$$`.
 */
function extractMath(markdown: string): {
  text: string;
  blocks: Array<{ token: string; html: string }>;
} {
  const blocks: Array<{ token: string; html: string }> = [];
  let i = 0;

  // Display math : $$...$$
  let text = markdown.replace(/\$\$([\s\S]+?)\$\$/g, (_, src) => {
    const token = `@@JR_MATH_BLOCK_${i++}@@`;
    blocks.push({ token, html: renderMathSafe(src.trim(), true) });
    return token;
  });

  // Inline math : $...$ (mais pas si précédé d'un \ — protection des $ littéraux)
  text = text.replace(/(?<!\\)\$([^\n$]+?)(?<!\\)\$/g, (_, src) => {
    const token = `@@JR_MATH_INLINE_${i++}@@`;
    blocks.push({ token, html: renderMathSafe(src.trim(), false) });
    return token;
  });

  return { text, blocks };
}

function injectMath(html: string, blocks: Array<{ token: string; html: string }>): string {
  let result = html;
  for (const { token, html: mathHtml } of blocks) {
    // Le token peut s'être retrouvé dans un <p>...</p>. On remplace tel quel.
    result = result.replace(token, mathHtml);
  }
  return result;
}

// `async: false` force le mode synchrone : marked.parse() renvoie une string.
// Ça nous permet d'utiliser <LessonContent> aussi bien dans des Server qu'en
// Client Components sans avoir à pré-rendre côté server.
const marked = new Marked({
  gfm: true,
  breaks: false,
  pedantic: false,
  async: false,
});

export type LessonContentProps = {
  /** Contenu Markdown (provient en général de la table lessons.content_md) */
  content: string;
  className?: string;
};

export function LessonContent({ content, className }: LessonContentProps) {
  const { text, blocks } = extractMath(content);
  const rawHtml = marked.parse(text) as string;
  const finalHtml = injectMath(rawHtml, blocks);

  return (
    <div
      className={cn(
        'prose prose-ink max-w-none',
        '[&_h2]:mt-8 [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:font-semibold',
        '[&_h3]:mt-6 [&_h3]:font-serif [&_h3]:text-xl [&_h3]:font-semibold',
        '[&_p]:my-4 [&_p]:leading-relaxed',
        '[&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6',
        '[&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6',
        '[&_code]:rounded [&_code]:bg-ink-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-sm',
        '[&_a]:text-primary-600 [&_a]:underline hover:[&_a]:text-primary-700',
        '[&_strong]:font-semibold [&_strong]:text-ink-900',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: finalHtml }}
    />
  );
}

// Helpers exportés pour les tests
export const __internal = { extractMath, injectMath, renderMathSafe };
